/** Login da equipe (Lara). Território escuro. Onda 12. */
import { useState } from "react";
import type { FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import type { SessionDTO } from "@unik/shared";
import { api, ApiClientError } from "../lib/api";
import { useSession } from "../lib/session";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { InstallPrompt } from "../ui/InstallPrompt";
import { FullScreenLoader } from "../ui/ProtectedRoute";
import { GoogleButton } from "../ui/GoogleButton";

export function Login() {
  const navigate = useNavigate();
  const { session, loading, setSession } = useSession();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <FullScreenLoader />;
  if (session?.actorType === "user") return <Navigate to="/gestao" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setFieldErrors({});
    try {
      const s = await api.post<SessionDTO>("/auth/login", { email, password });
      setSession(s);
      navigate("/gestao", { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
        if (err.fields) setFieldErrors(err.fields);
      } else {
        setError("Não foi possível entrar. Tente novamente.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="app-shell flex min-h-dvh flex-col items-center justify-center px-5">
      <div className="w-full max-w-sm">
        <header className="text-center">
          <img
            src="/brand/logotipo-principal-creme.png"
            alt="Unik"
            className="mx-auto h-14 w-auto"
          />
          <p className="type-overline mt-3 text-gold">Marca &amp; Posicionamento</p>
          <p className="type-body-sm mt-2 text-text-secondary">Acesso da equipe</p>
        </header>

        <div className="mt-8">
          <GoogleButton
            onSuccess={(s) => {
              setSession(s);
              navigate("/gestao", { replace: true });
            }}
            onError={(msg) => setError(msg)}
            label="Continuar com o Google"
            defaultEmail="laralivia925@gmail.com"
          />

          <div className="relative my-6 text-center">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border-subtle" />
            </div>
            <span className="relative bg-surface-base px-3 text-xs uppercase tracking-wider text-text-tertiary">
              ou com e-mail e senha
            </span>
          </div>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
          <Input
            label="E-mail"
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={fieldErrors.email}
            required
          />
          <Input
            label="Senha"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={fieldErrors.password}
            required
          />
          {error !== null && (
            <p role="alert" className="type-caption text-danger">
              {error}
            </p>
          )}
          <Button type="submit" fullWidth loading={submitting}>
            Entrar
          </Button>
        </form>
        <p className="type-caption mt-6 text-center text-text-tertiary">
          É cliente? <Link to="/entrar-cliente" className="text-gold">Entre por aqui</Link>
        </p>
        <div className="mt-8 flex justify-center">
          <InstallPrompt />
        </div>
      </div>
    </div>
  );
}
